/* eslint-disable react/jsx-props-no-spreading */
import type {
  ControllerRenderProps,
  FieldValues,
  UseFormReturn
} from 'react-hook-form';
import { memo, useCallback } from 'react';
import type { AddressAttributes } from '@library/types/addressAutocomplete';
import { AddressAutocompleteInput } from '@library/components/inputs';
import { useAddressAutocomplete } from '@bo-hooks/useAddressAutocomplete';

interface MemberAddressAutocompleteFormFieldProps {
  fieldProps: ControllerRenderProps<FieldValues>;
  formMethods: UseFormReturn<FieldValues>;
}

export const MemberAddressAutocompleteFormField = memo(
  ({ fieldProps, formMethods }: MemberAddressAutocompleteFormFieldProps) => {
    const { clearErrors, setValue } = formMethods;

    const { validAddresses } = useAddressAutocomplete({
      value: fieldProps.value as string
    });

    // Handle selected address from the autocomplete input
    const handleSelectedAddress = useCallback(
      (address: AddressAttributes) => {
        if (address) {
          const fields = [
            { name: 'address', value: address.address_label },
            { name: 'city', value: address.city },
            { name: 'state', value: address.state_code },
            { name: 'postalCode', value: address.postal_code }
          ];

          fields.forEach(({ name, value }) =>
            setValue(name, value || '', { shouldDirty: true })
          );

          clearErrors(['address', 'city', 'state', 'postalCode']);
        }
      },
      [clearErrors, setValue]
    );

    // Clear city, state, postalCode fields when the address input changes
    const handleAddressChange = useCallback(() => {
      setValue('city', '');
      setValue('state', '');
      setValue('postalCode', '');
    }, [setValue]);

    return (
      <AddressAutocompleteInput
        onSelectedAddress={handleSelectedAddress}
        onValueChange={handleAddressChange}
        validAddresses={validAddresses}
        {...fieldProps}
      />
    );
  }
);
